import type { AxiosInstance } from 'axios';
import type { SettingsApiToken, TokenAbility } from '@/types/settings';
import baseAxios from '@/helpers/forms/axios';

/**
 * The account settings endpoints: profile, contact information, photo,
 * security and API tokens. Everything that writes goes through `sendForm()`
 * instead, so only the reads — and the two calls whose response body is needed
 * verbatim — live here.
 *
 * `GET /settings/api/tokens` returns Spark `Token` models, whose abilities live
 * under `metadata.abilities`, and `GET /settings/api/token/abilities` returns
 * `{ name, value, default }` triples rather than bare strings.
 */
interface SettingsApi {
    fetchApiTokens: () => Promise<SettingsApiToken[]>
    fetchTokenAbilities: () => Promise<TokenAbility[]>
    createApiToken: (name: string, abilities: string[]) => Promise<string>
    enableTwoFactorAuth: (countryCode: string, phone: string) => Promise<string>
    disableTwoFactorAuth: () => Promise<void>
}

export default (axios: AxiosInstance = baseAxios): SettingsApi => ({
    fetchApiTokens: async (): Promise<SettingsApiToken[]> => {
        const response = await axios.get<SettingsApiToken[]>('/settings/api/tokens');
        return response.data;
    },
    fetchTokenAbilities: async (): Promise<TokenAbility[]> => {
        const response = await axios.get<TokenAbility[]>('/settings/api/token/abilities');
        return response.data;
    },
    /** Answers with the plaintext token, which is shown to the user exactly once. */
    createApiToken: async (name: string, abilities: string[]): Promise<string> => {
        const response = await axios.post<{ token: string }>('/settings/api/token', { name, abilities });
        return response.data.token;
    },
    /**
     * `POST /settings/two-factor-auth` answers with the emergency reset code as a
     * bare string body, which `sendForm` discards, so the two-factor screen needs
     * this wrapper to show the code once.
     */
    enableTwoFactorAuth: async (countryCode: string, phone: string): Promise<string> => {
        const response = await axios.post<string>('/settings/two-factor-auth', {
            country_code: countryCode,
            phone,
        });

        return response.data;
    },
    disableTwoFactorAuth: async (): Promise<void> => {
        await axios.delete('/settings/two-factor-auth');
    },
});
